import {
  browserPdfOutput,
  type DocumentPrinter,
  type FormRecordDetail,
  type FormRecordPage,
  type FormRecordQuery,
  type FormRecordSource,
  type FormRecordTreeView,
} from '@felix/ui';
import { api } from './api.js';

const id = encodeURIComponent;

/**
 * Ofis liste ucunun sorgusu (`GET /api/office/form-records`; ADR 0042 Ek U26): boş alanlar
 * gitmez, mantıksal süzgeçler "true" olarak.
 */
export function officeFormQuery(query: FormRecordQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === true) params.set(key, 'true');
    else if (value !== undefined && value !== null && value !== '' && value !== false) {
      params.set(key, String(value));
    }
  }
  return params.toString();
}

/** Kayıt dosyasının ofis ucu: dosya satırının kimliğiyle (doğrulanmış dosya; Ek U28, U30). */
function officeRecordFileUrl(recordId: string, fileId: string): string {
  return `/api/office/form-records/${id(recordId)}/files/${id(fileId)}`;
}

/**
 * Ofiste ISM form kayıtları (plan M8b Task 21; `apps/server/src/office/form-records.ts`): filo
 * listesi, ayrıntı, dosya ve ofis adımı. Ofis kaydı yazmaz (mimari belge §8); yalnız ofis adımına
 * parolayla karar verir. Saat UTC (ADR 0042 §16).
 */
export function officeFormSource(printer: DocumentPrinter, software: string): FormRecordSource {
  return {
    scope: 'web',
    list: (query) => {
      const search = officeFormQuery(query);
      return api<FormRecordPage>(`/office/form-records${search ? `?${search}` : ''}`);
    },
    detail: (recordId) => api<FormRecordDetail>(`/office/form-records/${id(recordId)}`),
    // ISM ağacında seçilen geminin formdaki kayıtları (ADR 0072); gemi seçilmeden istenmez
    tree: async (documentId, vesselId) => {
      if (!vesselId) {
        throw Object.assign(new Error('VESSEL_REQUIRED'), { code: 'VESSEL_REQUIRED' });
      }
      const query = new URLSearchParams({ vesselId, documentId });
      return api<FormRecordTreeView>(`/office/form-records/tree?${query}`);
    },
    fileUrl: (record, file) => officeRecordFileUrl(record.id, file.id ?? ''),
    downloadFile: async (record, file) => {
      const link = document.createElement('a');
      link.href = officeRecordFileUrl(record.id, file.id ?? '');
      link.download = file.name;
      link.rel = 'noopener';
      document.body.append(link);
      link.click();
      link.remove();
    },
    print: async () => {
      window.print();
    },
    printer,
    pdf: browserPdfOutput(),
    software,
    office: {
      decide: (recordId, input) =>
        api<FormRecordDetail>(`/office/form-records/${id(recordId)}/decision`, {
          method: 'POST',
          body: input,
        }),
      vessels: async () =>
        (await api<{ id: string; name: string }[]>('/office/vessels')).map((v) => ({
          id: v.id,
          name: v.name,
        })),
    },
  };
}
